<script lang="ts" setup>
import { ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { VForm } from "vuetify/components";

import OmniDialog from "@/components/omni/OmniDialog.vue";
import OrderPaymentItem from "@/components/order/OrderPayment.vue";
import { useOrderPayments } from "@/composables";

import type { OrderPayment } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { isLoading, createOrderPayment, createOrderPaymentDraft } = useOrderPayments();

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
  orderId: string;
}>();

const emit = defineEmits<{
  (e: "success", payment: OrderPayment): void;
}>();

const formRef = useTemplateRef<VForm>("form");
const payment = ref<OrderPayment>(createOrderPaymentDraft());

function close() {
  showDialog.value = false;
}

async function addPayment() {
  const validation = await formRef.value?.validate();

  if (!validation?.valid) {
    return;
  }

  const createdPayment = await createOrderPayment(props.orderId, {
    type: payment.value.type,
    rate: payment.value.rate,
  });

  if (!createdPayment) {
    return;
  }

  emit("success", createdPayment);
  close();
}

watch(showDialog, (value) => {
  if (!value) {
    return;
  }

  payment.value = createOrderPaymentDraft();
});
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :primary-action-label="t('general.add')"
    :primary-button-loading="isLoading"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isLoading"
    :title="t('payments.add-payment')"
    width="800"
    @primary-action:click="addPayment"
    @secondary-action:click="close"
  >
    <v-form ref="form">
      <order-payment-item v-model="payment" hide-delete />
    </v-form>
  </omni-dialog>
</template>
